import Controller from '@ember/controller';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action, get, set } from '@ember/object';

const TIME_RANGE_MS = {
  'Last 1 hour': 60 * 60 * 1000,
  'Last 24 hours': 24 * 60 * 60 * 1000,
  'Last 7 days': 7 * 24 * 60 * 60 * 1000,
  'Last 30 days': 30 * 24 * 60 * 60 * 1000,
};

function formatTimestampDate(epochMs) {
  return new Date(epochMs).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatTimestampTime(epochMs) {
  return new Date(epochMs).toLocaleString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  });
}

// Contract 3 uses "FAILURE"; our template checks for "FAILED"
function normalizeOutcome(outcome) {
  if (outcome === 'FAILURE') return 'FAILED';
  return outcome;
}

function mapTimelineEvent(raw) {
  return {
    eventId: raw.eventId,
    installId: raw.installId,
    processedAtMs: raw.envoyProcessedAt, // kept for chart bucketing
    processedDateDisplay: formatTimestampDate(raw.envoyProcessedAt),
    processedTimeDisplay: formatTimestampTime(raw.envoyProcessedAt),
    integrationType: raw.integrationType,
    locationName: raw.locationName ?? (raw.locationId != null ? String(raw.locationId) : null),
    operationType: raw.operation?.type,
    operationDefinition: raw.operation?.definition,
    operationStatus: normalizeOutcome(raw.outcome),
    failureType: raw.status ?? null,
    failureReason: raw.failureReason ?? null,
    direction: raw.direction,
    responsibleParty: raw.responsibleParty ?? null,
    primaryMetric: raw.primaryMetric ?? null,
    endpoint: raw.endpoint ?? null,
    durationMillis: raw.durationMillis,
  };
}

export default class IntegrationHealthDashboardController extends Controller {
  @tracked selectedTimeRange = 'Last 1 hour';
  @tracked selectedInstall = null; // null = All installs
  @tracked selectedEventType = 'All event types';
  @tracked selectedLocation = 'All locations';
  @tracked isLoading = false;
  @tracked isBackfilling = false;
  @tracked page = 1;
  @tracked expandedEventId = null;
  @tracked _hoveredDotIndex = null;
  @tracked selectedEvent = null;

  @service ajaxFetch;
  @service state;
  @service currentAdmin;

  perPage = 20;
  _refreshTimer = null;

  @tracked _healthData = null; // Contract 2: GET /api/v1/monitoring/health
  @tracked _timelineData = null; // Contract 3: GET /api/v1/debugging/timeline

  timeRangeOptions = ['Last 1 hour', 'Last 24 hours', 'Last 7 days', 'Last 30 days'];

  get _mappedEvents() {
    return (this._timelineData?.events ?? []).map(mapTimelineEvent);
  }

  get _meta() {
    return this._timelineData?.meta ?? { total: 0, page: 1, perPage: this.perPage };
  }

  get _integrations() {
    return this._healthData?.integrations ?? [];
  }

  get installOptions() {
    const isGlobal = this.currentAdmin.isGlobalAdmin;
    const myLocationId = this.state.currentLocation?.id;

    const installs = this._integrations
      .filter((i) => {
        // Location-only admins can only see integrations scoped to their location.
        // Company-scoped integrations (location === null) are global-admin-only.
        if (!isGlobal) {
          return i.location != null && String(i.location.id) === String(myLocationId);
        }
        return true;
      })
      .map((i) => ({
        label: i.location ? `${i.integrationName} — ${i.location.name}` : i.integrationName,
        value: i.installId,
        integrationName: i.integrationName,
        locationId: i.location?.id ?? null,
        locationName: i.location?.name ?? null,
      }));
    return [{ label: 'All integrations', value: '' }, ...installs];
  }

  get _selectedInstallData() {
    if (!this.selectedInstall) return null;
    const found = this._integrations.find((i) => i.installId === this.selectedInstall);
    if (!found) return null;
    return {
      value: found.installId,
      integrationName: found.integrationName,
      locationId: found.location?.id ?? null,
      locationName: found.location?.name ?? null,
    };
  }

  get locationOptions() {
    const seen = new Set();
    const locs = [];
    for (const i of this._integrations) {
      // Company-scoped integrations have no location — skip them here
      if (!i.location) continue;
      if (!seen.has(i.location.id)) {
        seen.add(i.location.id);
        locs.push({ label: i.location.name, value: Number(i.location.id) });
      }
    }
    return [{ label: 'All locations', value: 'All locations' }, ...locs];
  }

  get eventTypeOptions() {
    const seen = new Set();
    const types = [];
    for (const i of this._integrations) {
      for (const opType of i.activeOperationTypes ?? []) {
        if (!seen.has(opType)) {
          seen.add(opType);
          const label = opType
            .toLowerCase()
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (c) => c.toUpperCase());
          types.push({ label, value: label });
        }
      }
    }
    return [{ label: 'All event types', value: 'All event types' }, ...types];
  }

  get stats() {
    const summary = this._timelineData?.summary;

    const successRate = summary?.successRate != null ? `${summary.successRate}%` : '—';

    const total = summary?.total?.toLocaleString() ?? '—';
    const typeCounts = this._timelineData?.summary?.byType ?? {};

    return [
      { label: 'Success rate', value: successRate },
      { label: 'Total events', value: total },
      { label: 'Poll events', value: String(typeCounts['POLL'] ?? 0) },
      { label: 'Webhook events', value: String(typeCounts['WEBHOOK'] ?? 0) },
      { label: 'User Lookup events', value: String(typeCounts['USER_LOOKUP'] ?? 0) },
      { label: 'Credential Create', value: String(typeCounts['CREDENTIAL_CREATE'] ?? 0) },
    ];
  }

  // Shared chart computation — accessed by individual chart getters below.
  // Returns a plain object only used internally; never passed directly to the template.
  get _chartComputed() {
    const points = this._timelineData?.chartData ?? [];
    if (!points.length) return null;

    const rates = points.map((p) => p.successRate);
    const minRate = Math.min(...rates);
    const maxRate = Math.max(...rates);
    const is1hRange = get(this, 'selectedTimeRange') === 'Last 1 hour';
    // For 1h zoom in tightly so small variations are visible; other ranges use coarser steps
    const buffer = is1hRange ? 2 : 5;
    const step = is1hRange ? 5 : 10;
    const yMin = Math.max(0, Math.floor((minRate - buffer) / step) * step);
    const yMax = Math.min(100, Math.ceil((maxRate + buffer) / step) * step);
    const yRange = yMax - yMin || 1;

    const xScale = (i) => Math.round((i / Math.max(points.length - 1, 1)) * 624);
    const yScale = (rate) => Math.round(90 - ((rate - yMin) / yRange) * 80);

    const pts = points.map((p, i) => ({
      x: xScale(i),
      y: yScale(p.successRate),
      startMs: p.startMs,
      successRate: p.successRate,
    }));
    const linePath = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x},${p.y}`).join(' ');
    const areaPath = `${linePath} L ${pts[pts.length - 1].x},90 L ${pts[0].x},90 Z`;

    // Label step and format depend on selected time range.
    // Bucket counts from the backend: 1h=12x5m | 24h=24x1h | 7d=21x8h | 30d=30x1d
    const is1h = get(this, 'selectedTimeRange') === 'Last 1 hour';
    const is24h = get(this, 'selectedTimeRange') === 'Last 24 hours';
    const is7d = get(this, 'selectedTimeRange') === 'Last 7 days';
    // 1h: label every bucket (5m); 24h: label every 4 buckets (4h); 7d: label every 3 buckets (1 day); 30d: label every 5 buckets (5 days)
    const labelStep = is1h ? 1 : is24h ? 4 : is7d ? 3 : 5;
    const labelIndices = [];
    for (let i = 0; i < pts.length; i++) {
      if (i % labelStep === 0) labelIndices.push(i);
    }
    if (labelIndices[labelIndices.length - 1] !== pts.length - 1) {
      labelIndices.push(pts.length - 1);
    }
    const lastIdx = pts.length - 1;
    const xLabels = labelIndices.map((i, li) => ({
      x: pts[i].x,
      label:
        i === lastIdx
          ? 'Now'
          : is1h || is24h
            ? new Date(pts[i].startMs).toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
            : new Date(pts[i].startMs).toLocaleString('en-US', { month: 'short', day: 'numeric' }),
      textAnchor: li === 0 ? 'start' : li === labelIndices.length - 1 ? 'end' : 'middle',
    }));

    const mid = Math.round((yMax + yMin) / 2);
    const gridLines = [yMax, mid, yMin].map((rate) => ({
      y: yScale(rate),
      labelY: yScale(rate) + 4,
      label: `${rate}%`,
    }));

    return { linePath, areaPath, dots: pts, xLabels, gridLines };
  }

  // Individual chart getters expose only primitives/arrays to the template,
  // avoiding Ember's "dynamic component definition" error when returning POJOs.
  get hasChartData() {
    return this._chartComputed !== null;
  }
  get chartLinePath() {
    return this._chartComputed?.linePath ?? '';
  }
  get chartAreaPath() {
    return this._chartComputed?.areaPath ?? '';
  }
  get chartGridLines() {
    return this._chartComputed?.gridLines ?? [];
  }
  get chartDots() {
    return this._chartComputed?.dots ?? [];
  }
  get chartXLabels() {
    return this._chartComputed?.xLabels ?? [];
  }

  get chartHoveredPoint() {
    if (this._hoveredDotIndex === null) return null;
    const dots = this._chartComputed?.dots ?? [];
    const dot = dots[this._hoveredDotIndex];
    if (!dot) return null;

    const d = new Date(dot.startMs);
    const datePart = d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const timePart = d.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    const dateValue = `${datePart}, ${timePart}`;

    const tooltipW = 112;
    const tooltipH = 34;
    const tooltipX = Math.min(Math.max(dot.x - tooltipW / 2, 0), 624 - tooltipW);
    const tooltipY = dot.y - tooltipH - 6 < 2 ? dot.y + 6 : dot.y - tooltipH - 6;

    return {
      x: dot.x,
      y: dot.y,
      dateValue,
      successRate: dot.successRate,
      tooltipX,
      tooltipY,
      tooltipW,
      tooltipH,
      labelX: tooltipX + 7,
      valueX: tooltipX + tooltipW - 7,
      row1Y: tooltipY + 13,
      row2Y: tooltipY + 27,
    };
  }

  @action onChartMouseMove(event) {
    const svg = event.currentTarget;
    const rect = svg.getBoundingClientRect();
    const mouseX = ((event.clientX - rect.left) / rect.width) * 624;
    const dots = this._chartComputed?.dots ?? [];
    if (!dots.length) return;
    let closest = 0;
    let minDist = Infinity;
    for (let i = 0; i < dots.length; i++) {
      const dist = Math.abs(dots[i].x - mouseX);
      if (dist < minDist) {
        minDist = dist;
        closest = i;
      }
    }
    this._hoveredDotIndex = closest;
  }

  @action onChartLeave() {
    this._hoveredDotIndex = null;
  }

  get filteredEvents() {
    return this._mappedEvents;
  }

  get recordCount() {
    return this._meta.total;
  }

  get pageCount() {
    return Math.ceil(this.recordCount / this.perPage);
  }

  get paginatedEvents() {
    return this._mappedEvents;
  }

  startAutoRefresh() {
    this.stopAutoRefresh();
    this._refreshTimer = setInterval(() => this.fetchData(), 60_000);
  }

  stopAutoRefresh() {
    if (this._refreshTimer) {
      clearInterval(this._refreshTimer);
      this._refreshTimer = null;
    }
  }

  willDestroy() {
    super.willDestroy(...arguments);
    this.stopAutoRefresh();
  }

  async fetchData() {
    // eslint-disable-next-line no-console
    console.log('[IHD] currentLocation', {
      id: this.state.currentLocation?.id,
      name: this.state.currentLocation?.name,
    });
    this.isLoading = true;
    const now = Date.now();
    const startTime = now - TIME_RANGE_MS[get(this, 'selectedTimeRange')];

    const params = {
      startTime: String(startTime),
      endTime: String(now),
      page: String(this.page),
      perPage: String(this.perPage),
    };
    // eslint-disable-next-line ember/use-ember-get-and-set
    if (this.selectedInstall != null) params.installId = String(this.selectedInstall);
    // eslint-disable-next-line ember/use-ember-get-and-set
    if (this.selectedEventType !== 'All event types') params.eventType = this.selectedEventType;
    // eslint-disable-next-line ember/use-ember-get-and-set
    if (this.selectedLocation !== 'All locations') params.location = String(this.selectedLocation);

    try {
      const [healthData, timelineData] = await Promise.all([
        this.ajaxFetch.request('/api/v1/monitoring/health'),
        this.ajaxFetch.request('/api/v1/debugging/timeline', { data: params }),
        new Promise((resolve) => setTimeout(resolve, 500)),
      ]);
      this._healthData = healthData;
      this._timelineData = timelineData;
    } finally {
      this.isLoading = false;
    }
  }

  @action setTimeRange(value) {
    set(this, 'selectedTimeRange', value);
    this.page = 1;
    this.fetchData();
  }

  @action setEventType(value) {
    this.selectedEventType = value;
    this.page = 1;
    this.fetchData();
  }
  @action setLocation(value) {
    this.selectedLocation = value === 'All locations' ? 'All locations' : Number(value);
    this.selectedInstall = null;
    this.page = 1;
    this.fetchData();
  }
  @action setPage(value) {
    this.page = value;
    if (value === 1) {
      this.startAutoRefresh();
    } else {
      this.stopAutoRefresh();
    }
    this.fetchData();
  }
  @action toggleExpand(eventId) {
    this.expandedEventId = this.expandedEventId === eventId ? null : eventId;
  }

  @action openDrawer(event) {
    if (event.operationStatus === 'OK') return;
    this.selectedEvent = event;
  }

  @action closeDrawer() {
    this.selectedEvent = null;
  }

  @action setInstall(value) {
    this.selectedInstall = value === 'null' || value === '' ? null : Number(value);
    // Auto-select the install's location. Normalize to Number for consistent eq comparisons in the template.
    // Company-scoped installs (no location) reset to "All locations".
    const installData = this._integrations.find((i) => i.installId === this.selectedInstall);
    this.selectedLocation = installData?.location ? Number(installData.location.id) : 'All locations';
    this.page = 1;
    this.fetchData();
  }

  @action async queueBackfill() {
    this.isBackfilling = true;
    const now = Date.now();
    const startTime = now - TIME_RANGE_MS[get(this, 'selectedTimeRange')];
    try {
      await this.ajaxFetch.request('/api/v1/backfill', {
        type: 'POST',
        contentType: 'application/json',
        data: { installId: this.selectedInstall, startTime, endTime: now },
      });
    } finally {
      this.isBackfilling = false;
    }
  }
}
